<template>
  <div class="w-100 bg-white">
    <Navbar />
    <block :height="83"></block>
    <div class="ga-container d-block pb-5">
      <div class="row justify-content-center">
        <!-- STEPPER -->
        <div class="col-xl-7 col-12 mb-5">
          <stepper :step="step" :need_payment="payment.status != 'PAY_LATER'"></stepper>
        </div>

        <div class="col-xl-7 col-12 mb-5">
          <div class="w-100 radius-12 bg-white box-shadow-light-aff border-semi-grey p-4 mb-5">
            <div v-if="is_load || is_loading_payment" class="w-100 py-4 text-center">
              <loader />
              <p v-if="is_loading_payment" class="mt-2 body-2-4">Loading payment data..</p>
            </div>
            <template v-else>
              <div class="w-100 pt-4" v-if="error">
                <ErrorMessage :message="error"></ErrorMessage>
              </div>
              <div v-else class="w-100">
                <!-- PAYMENT SUCCESS -->
                <div v-if="show_success_data" class=" w-100">
                  <div class="mb-5 text-center" v-if="payment.status == 'SUCCESS' || payment.status == 'PAY_LATER'">
                    <img src="../../../../assets/img/icon/green-checked-2.png" class="mb-4 img-check">
                    <p class="heading-5-2 text-green mb-0 text-uppercase">{{ $t('ticket.your-booking-complete') }}</p>
                    <p class="mb-0 body-2-2-1 mb-1">{{ $t('ticket.thanks-for-order') }}</p>
                    <p class="mb-0 body-2-4 mb-3">Invoice : {{ trx.invoice }}</p>
                    <p class="mb-0 text-24 font-lato text-ticket">{{ $formatCurrency(total_bill) }}</p>
                  </div>

                  <div class="mb-5 text-center" v-if="payment.status == 'CANCELLED'">
                    <img src="../../../../assets/img/icon/cancel-red.png" class="mb-4 img-check">
                    <p class="heading-5-2 text-danger mb-0 text-uppercase">{{ $t('ticket.your-booking-cancelled') }}</p>
                    <p class="mb-0 body-2-4 mb-0">Invoice : {{ trx.invoice }}</p>
                  </div>

                  <div class="mb-5 text-center" v-if="payment.status == 'PENDING' || isHostedPaymentPending">
                    <img src="../../../../assets/img/icon/pending2.png" class="mb-4 img-check">
                    <p class="heading-5-2 mb-0 text-uppercase text-warning">{{ isHostedPaymentPending ? 'Payment Pending' : 'Waiting Confirmation' }}</p>
                    <p class="mb-0 body-2-2-1 mb-1">{{ isHostedPaymentPending ? 'Please complete your payment to finalize the booking.' : 'Please wait, your booking is being checked' }}</p>
                    <p class="mb-0 body-2-4 mb-3">Invoice : {{ trx.invoice }}</p>
                    <button v-if="isHostedPaymentPending" class="g-btn g-btn-primary g-btn-md px-4 mx-auto" @click="handlePaymentClick">Complete Payment</button>
                  </div>

                  <div class="row mb-4">
                    <!-- PRODUCT -->
                    <div class="col-md-8 col-12 mb-4">
                      <div class="w-100 border-form radius-6 h-100 position-relative" style="padding-bottom: 56.667px;">
                        <div class="p-3 border-b-form radius-t-6 position-relative">
                          <div class="row no-gutters z-index-1026">
                            <div class="col-8">
                              <p class="mb-0 body-1-4">Service</p>
                            </div>
                            <div class="col-4 text-right">
                              <p class="mb-0 body-1-4">{{ $t('ticket.price') }}</p>
                            </div>
                          </div>
                          <div class="primary-overlay z-index-1025"></div>
                        </div>
                        <div class="p-3">
                          <p class="mb-3 body-3-3"><i>The service time is displayed in the property's local time: (UTC{{ getMerchantTimezone.offset }}) {{ getMerchantTimezone.name }}.</i></p>
                          <div class="row no-gutters" v-for="(item,index) in trx.tx_detail_list" :class="{'mb-3': index+1 < trx.tx_detail_list.length }">
                            <div class="col-1 align-self-start">
                              <p class="mb-0 font-lato text-14">{{ item.qty }}X</p>
                            </div>
                            <div class="col-7 align-self-start">
                              <div class="pl-mobile">
                                <p class="mb-0 body-2-1">{{ item.product_name }}</p>
                                <ul class="pl-3">
                                  <li class="mb-1">
                                    <p class="mb-0 body-3-3">{{ $formatDateTime(item.service_date, true) }}</p>
                                  </li>
                                  <li v-if="$isNotEmptyV2(item.guest_display)">
                                    <p class="mb-0 body-3-3">Guest : {{ item.guest_display }}<span class="text-subcolor" v-if="item.qty > 1"> ({{ item.total_guest_all }} total)</span></p>
                                  </li>
                                  <li v-if="$isNotEmpty(item.detail.place_choice)">
                                    <p class="mb-0 body-3-3">Place : {{ item.detail.place_choice }}</p>
                                  </li>
                                  <li v-if="$isNotEmpty(item.detail.therapist_gender) && item.detail.therapist_gender != 'Random'">
                                    <p class="mb-0 body-3-3">Preferred gender of therapist : {{ item.detail.therapist_gender }}</p>
                                  </li>
                                </ul>
                              </div>
                            </div>
                            <div class="col-4 align-self-start text-right">
                              <p class="mb-0 font-lato text-14">{{ $formatCurrency(item.subtotal_sell) }}</p>
                            </div>
                          </div>
                        </div>
                        <div class="p-3 border-t-d-secondary-t position-absolute w-100" style="bottom: 0;">
                          <div class="row no-gutters">
                            <div class="col-8">
                              <p class="mb-0 body-1-4">{{ $t('ticket.total') }}</p>
                            </div>
                            <div class="col-4">
                              <p class="mb-0 body-1-4 text-right">{{ $formatCurrency(Number(trx.total_payment)) }}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- GUEST DETAIL -->
                    <div class="col-md-4 col-12 mb-4">
                      <div class="w-100 border-form radius-6 h-100">
                        <div class="p-3 border-b-form radius-t-6 position-relative">
                          <div class="row no-gutters z-index-1026">
                            <div class="col-12">
                              <p class="mb-0 body-1-4">{{ $t('ticket.guest-detail') }}</p>
                            </div>
                          </div>
                          <div class="primary-overlay z-index-1025"></div>
                        </div>
                        <div class="p-3">
                          <div class="row no-gutters">
                            <div class="col-12 mb-3">
                              <div class="w-100 pr-3">
                                <p class="mb-0 body-3-3-1">{{ $t('ticket.name') }}</p>
                                <p class="mb-0 body-2-1">{{ trx.customer_detail.full_name }}</p>
                              </div>
                            </div>
                            <div class="col-12 mb-3">
                              <div class="w-100">
                                <p class="mb-0 body-3-3-1">{{ $t('ticket.email') }}</p>
                                <p class="mb-0 body-2-1">{{ trx.customer_detail.email }}</p>
                              </div>
                            </div>
                            <div class="col-12">
                              <div class="w-100">
                                <p class="mb-0 body-3-3-1">{{ $t('ticket.phone-number') }}</p>
                                <p class="mb-0 body-2-1">{{ trx.customer_detail.phone_number }}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- PAYMENT -->
                    <div class="col-12">
                      <div class="w-100 border-form radius-6 h-100">
                        <div class="p-3 border-b-form radius-t-6 position-relative">
                          <div class="row no-gutters z-index-1026">
                            <div class="col-12">
                              <p class="mb-0 body-1-4">{{ $t('ticket.payment-detail') }}</p>
                            </div>
                          </div>
                          <div class="primary-overlay z-index-1025"></div>
                        </div>
                        <div class="p-3">
                          <template v-if="payment.status == 'PAY_LATER'">
                            <p class="mb-0 body-2-1">This booking uses <b>pay later</b></p>
                          </template>
                          <template v-else>
                            <div class="row">
                              <div class="col-lg-4 col-5">
                                <p class="mb-0 body-2-1">{{ $t('ticket.payment-method') }}</p>
                              </div>
                              <div class="col-lg-8 col-7 text-right">
                                <p class="mb-0 body-2-1">{{ payment.detail.payment_channel.name }}</p>
                              </div>
                            </div>
                            <div class="w-100 my-3 border-t-d-secondary-t"></div>
                            <div class="row">
                              <div class="col-lg-4 col-5">
                                <p class="mb-0 body-2-1">{{ $t('ticket.payment-time') }}</p>
                              </div>
                              <div class="col-lg-8 col-7 text-right">
                                <p class="mb-0 body-2-1">{{ $formatDateTime(payment.created_at) }}</p>
                              </div>
                            </div>
                            <template v-if="payment_surcharge > 0">
                              <div class="w-100 my-3 border-t-d-secondary-t"></div>
                              <div class="row">
                                <div class="col-lg-4 col-5">
                                  <p class="mb-0 body-2-1">{{ $t('ticket.payment-surcharge') }}</p>
                                </div>
                                <div class="col-lg-8 col-7 text-right">
                                  <p class="mb-0 body-2-1">{{ $formatCurrency(payment_surcharge) }}</p>
                                </div>
                              </div>
                            </template>
                            <div class="w-100 my-3 border-t-d-secondary-t"></div>
                            <div class="row">
                              <div class="col-lg-4 col-5">
                                <p class="mb-0 body-2-1">{{ $t('ticket.total-paid') }}</p>
                              </div>
                              <div class="col-lg-8 col-7 text-right">
                                <p class="mb-0 body-2-1">{{ $formatCurrency(total_bill) }}</p>
                              </div>
                            </div>
                            <div class="w-100 my-3 border-t-d-secondary-t"></div>
                            <div class="row">
                              <div class="col-lg-4 col-5">
                                <p class="mb-0 body-2-1">{{ $t('ticket.payment-status') }}</p>
                              </div>
                              <div class="col-lg-8 col-7 text-right">
                                <p class="mb-0 body-2-4 text-success text-uppercase" v-if="payment.status == 'SUCCESS'">{{ $t('ticket.success') }}</p>
                                <p class="mb-0 body-2-4 text-danger text-uppercase" v-if="payment.status == 'CANCELLED'">{{ $t('ticket.cancelled') }}</p>
                                <p class="mb-0 body-2-4 text-warning text-uppercase" v-if="payment.status == 'PENDING' || payment.status == 'UNPAID'">pending</p>
                              </div>
                            </div>
                          </template>
                        </div>
                      </div>
                    </div>

                    <div class="col-12 mt-5" v-if="payment.status == 'PENDING'">
                      <div class="w-100 text-center">
                        <p class="body-2-4 mb-3">{{$t("hotel.waiting.already-made-a-payment")}}</p>
                          <state-button variant="primary-ticket" size="lg" :click="checkStatus">{{$t("hotel.waiting.check-payment-status")}}</state-button>
                          <p class="body-2-4 text-danger mb-0 mt-2" v-if="payment_still_not_complete">{{$t("hotel.waiting.payment-still-not-processed")}}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <template v-else>
                  <!-- PAYMENT UNPAID & PENDING -->
                  <div v-if="payment.status == 'UNPAID' || payment.status == 'PENDING'" class="w-100">
                    <template v-if="payment.status == 'UNPAID'">
                      <VirtualAccount
                        v-if="payment.payment_method == 'VIRTUAL_ACCOUNT' || payment.payment_method == 'BANK_TRANSFER'"
                        :data="payment.detail"
                        module_page="HOTEL_CONCIERGE"
                        :customer_detail="trx.customer_detail" />
                      <Qris
                        v-if="payment.payment_method == 'QRIS'"
                        :data="payment.detail"
                        module_page="HOTEL_CONCIERGE"
                        :customer_detail="trx.customer_detail" />

                      <div class="w-100 text-center mt-5">
                        <button class="g-btn g-btn-secondary g-btn-lg mb-3 w-100" v-if="payment.bank_name == 'DIRECT_PAYMENT'" @click="toggleConfirmPayment()">Confirm Payment</button>
                        <template v-else>
                          <button class="g-btn g-btn-secondary g-btn-lg mb-3 w-100" v-if="isJokulCreditCardPending" :disabled="is_opening_jokul_checkout" @click="openJokulCheckout()">{{$t('hotel.action.continue-payment')}}</button>
                          <p class="body-2-4 mb-3">{{$t("hotel.waiting.already-made-a-payment")}}</p>
                          <state-button variant="primary-ticket" size="lg" :click="checkStatus">{{$t("hotel.waiting.check-payment-status")}}</state-button>
                          <p class="body-2-4 text-danger mb-0 mt-2" v-if="payment_still_not_complete">{{$t("hotel.waiting.payment-still-not-processed")}}</p>
                        </template>
                      </div>

                      <!-- <button class="g-btn g-btn-secondary g-btn-lg mb-3 w-100" v-if="payment.bank_name == 'DIRECT_PAYMENT'" @click="toggleConfirmPayment()">Confirm Payment</button> -->

                      <!-- DIRECT PAYMENT CONFIRM -->
                      <popup-detail
                        direction="bottom"
                        :show="confirm_payment_show"
                        wrapper_size="mobile"
                        title="Confirm your payment"
                        @close="toggleConfirmPayment">
                        <template v-slot:body>
                          <div class="p-4 border-t-semi-grey">
                            <PaymentConfirm ref="payment_confirm" v-if="confirm_payment_show" :tx_order_id="tx_id" @close="toggleConfirmPayment"></PaymentConfirm>
                          </div>
                        </template>
                      </popup-detail>
                    </template>
                    <template v-else>
                      <div class="w-100 px-4 py-5 bg-orange-soft mb-4 text-center">
                        <img src="../../../../assets/img/icon/pending.png" width="100" class="position-relative mx-auto mb-4">
                        <p class="font-bold mb-3 text-16 text-orange py-2 px-4 bg-white mx-auto d-table" style="border-radius:19px;">Waiting Confirmation</p>
                        <p class="font-bold mb-0 text-16 text-white w-100 px-3">Please wait, your booking is being checked</p>
                      </div>
                      <div class="px-4">
                        <div class="w-100 mb-4 bg-white radius-12 p-3 border-semi-grey">
                          <div class="row no-gutters mb-3 pb-3 border-b-semi-grey cursor-pointer" @click="toggleDetail">
                            <div class="col-2 align-self-center">
                              <img :src="getMerchant.image_url" class="w-100">
                            </div>
                            <div class="col-9 align-self-center">
                              <div class="px-3">
                                <p class="body-1-2 mb-0">{{getMerchant.name}}</p>
                                <!-- <p class="body-3-3-1 mb-0">{{total_ordered}}</p> -->
                              </div>
                            </div>
                            <div class="col-1 align-self-center text-right">
                              <i class="iconsmind-Arrow-Right font-weight-bold text-18"></i>
                            </div>
                          </div>
                          <div class="row no-gutters">
                            <div class="col-7">
                              <p class="body-3-3-1 mb-0">{{$t("general.payment-method")}}</p>
                              <p class="body-2-1 mb-0">{{payment.detail.payment_channel.name}}</p>
                            </div>
                            <div class="col-5 text-right">
                              <p class="body-3-3-1 mb-0">{{$t('general.payment-method')}}</p>
                              <p class="sub-heading-1-2 mb-0">{{$formatCurrency(payment.detail.total_paid)}}</p>
                            </div>
                          </div>
                        </div>
                        <button class="g-btn g-btn-primary g-btn-lg w-100" @click="checkStatus()">{{$t('hotel.action.check-payment-status')}}</button>
                      </div>
                    </template>
                  </div>

                  <!-- PAYMENT FAILED -->
                  <div v-if="payment.status == 'CANCELLED'" class="w-100 py-5">
                    <div class="px-4">
                      <div class="mx-auto failed-icon-rounded-wrap mb-4">
                        <div class="mx-auto failed-icon-rounded">
                        <img src="../../../../assets/img/icon/close-white.png" height="50" width="50">
                        </div>
                      </div>
                      <div class="text-center mb-5">
                        <p class="heading-5 text-danger mb-2">{{$t('general.payment-failed')}}</p>
                        <p class="body-1-3 mb-0">{{$t("hotel.concierge.fitur.your-transaction-has-failed")}}</p>
                      </div>

                      <!-- JOKUL CC -->
                      <template v-if="payment.bank_name == 'JOKUL_DOKU' && payment.payment_method == 'CREDIT_CARD'">
                        <p class="body-3-3-1 mb-1">{{$t('hotel.concierge.fitur.detail-error')}}</p>
                        <table class="table table-bordered">
                          <tbody>
                            <tr>
                              <td class="body-3-3-1">{{$t('general.payment-method')}}</td>
                              <td class="body-3-3-2">{{payment.detail.payment_channel.name}}</td>
                            </tr>
                            <tr>
                              <td class="body-3-3-1">{{$t("hotel.concierge.fitur.masked-card-number")}}</td>
                              <td class="body-3-3-2">{{payment.payment_detail_error.card_payment.masked_card_number}}</td>
                            </tr>
                            <tr>
                              <td class="body-3-3-1">{{$t("hotel.concierge.fitur.response-code")}}</td>
                              <td class="body-3-3-2">{{payment.payment_detail_error.card_payment.response_code}}</td>
                            </tr>
                            <tr>
                              <td class="body-3-3-1">{{$t("hotel.concierge.fitur.response-message")}}</td>
                              <td class="body-3-3-2">{{payment.payment_detail_error.card_payment.response_message}}</td>
                            </tr>
                            <tr>
                              <td class="body-3-3-1">{{$t("hotel.concierge.fitur.issuer")}}</td>
                              <td class="body-3-3-2">{{payment.payment_detail_error.card_payment.issuer}}</td>
                            </tr>
                          </tbody>
                        </table>
                      </template>

                      <button class="g-btn g-btn-secondary g-btn-lg w-100" @click="goToHome()">{{$t('general.back-to-home')}}</button>
                    </div>
                  </div>
                </template>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
    <Footer></Footer>
  </div>
</template>

<script>
  import Navbar from '@/components/hotel/concierge/general/Navbar';
  import Footer from '@/components/hotel/concierge/general/Footer';
  import stepper from '@/components/general/stepper';
  import { mapGetters } from 'vuex'
	import {get} from '@/services/index.js'
	import VirtualAccount from '@/components/general/payment/VirtualAccount'
	import Qris from '@/components/general/payment/Qris'
  import PaymentConfirm from '@/components/general/payment/PaymentConfirm'
  import { groupTxDetailByProduct } from '@/utils/activityCart.js'
  import { trackPageViewAfterDataLoaded } from '../../../../utils/analytics';
  import { applyResolvedTitle } from '../../../../utils/titleService';

  export default {
    props:{
      parent_data:{
        type: Object,
        default: () => {}
      }
    },
    components:{
      stepper,
      Navbar,
      Footer,
      VirtualAccount,
      Qris,
      PaymentConfirm
    },
    data() {
      return {
        is_load: false,
        error: '',
        step: 3,
        original_data: {},
        trx: {},
        payment: {},
				show_detail: false,
				confirm_payment_show: false,
				merchant_booked: {},
				service_date: null,
        payment_still_not_complete: false,
        is_loading_payment: false,
        has_tried_checking_payment: false,
        has_opened_jokul_checkout: false,
        is_opening_jokul_checkout: false
      }
    },
    computed:{
			...mapGetters(['getMerchant', 'getMerchantTimezone']),
      jokulCheckoutUrl(){
        let pg_response = this.payment.pg_response
        return (pg_response && pg_response.payment && pg_response.payment.url) || ''
      },
      isJokulCreditCardPending(){
        let is_status_pending = this.payment.status == 'UNPAID' || this.payment.status == 'PENDING'
        let is_jokul_cc = this.$isJokulCreditCard(this.payment.bank_name, this.payment.payment_method)
        return is_status_pending && is_jokul_cc && this.jokulCheckoutUrl !== ''
      },
			tx_id(){
				return this.$route.params.trx_id
			},
			payment_surcharge(){
				let data = 0
				if(this.payment.status && this.payment.status != 'PAY_LATER'){
					data = this.payment.detail.payment_surcharge
				}
				return Number(data)
			},
			total_bill(){
				let data = 0
				if(this.trx.id){
					data = Number(this.trx.total_payment)
				}
				if(this.payment_surcharge > 0){
					data += this.payment_surcharge
				}
				return data
			},
      isFlywirePayment() {
        const { bank_name, payment_method, status } = this.payment;
        return bank_name === 'FLYWIRE' && payment_method === 'CREDIT_CARD' && (status === 'PENDING' || status === 'UNPAID');
      },
      isRazorpayCurlecPayment() {
        const { bank_name, payment_method, status } = this.payment;
        return bank_name === 'RAZORPAY_CURLEC' && (payment_method === 'CREDIT_CARD' || payment_method === 'DIRECT_DEBIT') && (status === 'PENDING' || status === 'UNPAID');
      },
      isCommercePay() {
        const { bank_name, status } = this.payment;
        return bank_name === 'COMMERCEPAY' && (status === 'PENDING' || status === 'UNPAID');
      },
      isPivotPayment() {
        const { bank_name, status } = this.payment;
        return this.$isPivotPayment(bank_name) && (status === 'PENDING' || status === 'UNPAID');
      },
      isHostedPaymentPending() {
        return this.isFlywirePayment || this.isRazorpayCurlecPayment || this.isCommercePay || this.isPivotPayment;
      },
			show_success_data(){
				const { status, bank_name } = this.payment;
        const isPaid = status === 'SUCCESS';
        const isPayLater = status === 'PAY_LATER';
        const isDirectPending = status === 'PENDING' && bank_name === 'DIRECT_PAYMENT';

				return isPaid || isPayLater || isDirectPending || this.isHostedPaymentPending;
			},
		},
    mounted(){
      this.loadFirstData()
    },
    methods:{
      loadFirstData() {
				this.is_load = true
				this.error = ''
				this.original_data = {}
				this.trx = {}
				this.payment = {}

				Promise.all([
					this.loadTrxDetailFromAPI()
				]).then(res=>{
          let trx_res = res[0]

          this.getDetailPayment(trx_res.tx_payment_list)
          this.autoOpenJokulCheckout()

					let clone_data = this.$deepCopy(trx_res)
          delete clone_data.tx_payment_list

					this.original_data = trx_res
					this.trx = clone_data

          this.trx.customer_detail.full_name = clone_data.customer_detail.first_name
          if(this.$isNotEmpty(clone_data.customer_detail.last_name)){
            this.trx.customer_detail.full_name += ` ${clone_data.customer_detail.last_name}`
          }

          this.setPageTitleAndTrack();

          // Cart mengirim 1 baris per unit guest, jadi digabung lagi supaya tidak tampil berulang
          this.trx.tx_detail_list = groupTxDetailByProduct(trx_res.tx_detail_list.map(x=>{
            x.detail = JSON.parse(x.detail)
            return x
          }))
        }).then(() => {
          if((this.isFlywirePayment || this.isRazorpayCurlecPayment) && !this.has_tried_checking_payment){
            this.has_tried_checking_payment = true
            this.is_loading_payment = true
            setTimeout(() => {
              this.checkStatus().then(() => {
                this.is_loading_payment = false
              })
            }, 5000)
          }
        }).catch(err=>this.error = err).finally(()=>this.is_load = false)
			},
			loadTrxDetailFromAPI(check_parent_data=true){
        return new Promise((success,fail)=>{
          const hitAPI = () => {
            const routeQuery = this.$route.query;
            let params = {
              merchant_id: this.getMerchant.id
            }
            if(this.$isNotEmptyV2(routeQuery) && this.$isNotEmptyV2(routeQuery.tx_sk)){
              params.tx_sk = routeQuery.tx_sk
            }
            get("CORE_HOTEL",`merchant-pos/transaction/${this.tx_id}`,params).then(response=>{
              let res = response.data
              if(res.success){
                success(res.data)
              }else{
                fail(res.message)
              }
            })
          }

          if(check_parent_data && this.parent_data.id){
            success(this.parent_data)
          }else{
            hitAPI()
          }
				})
			},
			getDetailPayment(payment){
				if(payment.length == 0){
					this.payment.status = 'PAY_LATER'
          this.step = 4
				}else{
					let clone_data = this.$deepCopy(payment[0].tx_bill_payment)
					this.payment = {
						status: clone_data.status,
						bank_name: clone_data.payment_channel.payment_gateway.pg_company,
						pg_mode: clone_data.payment_channel.payment_gateway.pg_mode,
						payment_method: clone_data.payment_channel.channel,
						payment_code: clone_data.payment_channel.ch_ref1,
						pg_response: JSON.parse(clone_data.inq_resp_detail),
						created_at: payment[0].created_at,
						detail: clone_data,
						payment_detail_error: clone_data.pay_resp_detail == null ? null : JSON.parse(clone_data.pay_resp_detail)
					}
          if(this.payment.status != 'UNPAID') this.step = 4
				}
			},
      setPageTitleAndTrack(){
        const { status = "PENDING" } = this.payment;
        const { pos: { tagline = null } } = this.trx;
        const isPaymentPending = status === 'PENDING';
        const isPaymentCancelled = status === 'CANCELLED';

        let title = 'Booking Confirmed';

        if(isPaymentPending) title = 'Payment Pending';
        if(isPaymentCancelled) title = 'Booking Failed';
        const params = {
          router: this.$router,
          title: tagline ? `${title} - ${tagline}` : title,
          trackFn: () => trackPageViewAfterDataLoaded()
        }

        applyResolvedTitle(params);
      },
      // Popup Jokul dibuka sekali saja per kunjungan; refetch status tidak boleh membukanya lagi
      autoOpenJokulCheckout(){
        if(!this.isJokulCreditCardPending || this.has_opened_jokul_checkout) return
        this.has_opened_jokul_checkout = true
        this.openJokulCheckout()
      },
      openJokulCheckout(){
        this.is_opening_jokul_checkout = true
        this.$openJokulCheckout(this.jokulCheckoutUrl).finally(()=>{
          this.is_opening_jokul_checkout = false
        })
      },
      checkStatus(){
        return new Promise((success,fail)=>{
          this.payment_still_not_complete = false
          this.loadTrxDetailFromAPI(false)
          .then(res=>{
            if(res.tx_payment_list[0].tx_bill_payment.status === 'UNPAID') this.payment_still_not_complete = true
            else {
              this.getDetailPayment(res.tx_payment_list)
              this.step = 4
            }
          })
          .finally(()=>success())
        })
      },
			toggleDetail(){
				this.show_detail = !this.show_detail
			},
			goToHome(){
				this.$router.push(`/${this.getMerchant.code}`)
			},
			toggleConfirmPayment(){
				let is_payment_confirm_exist = this.$refs.payment_confirm
				if(this.confirm_payment_show && is_payment_confirm_exist && is_payment_confirm_exist.result_push == 'success'){
					this.loadFirstData()
				}
				this.confirm_payment_show = !this.confirm_payment_show
			},
      handlePaymentClick(){
        const { pg_response, payment_method: channel, payment_code: code } = this.payment;

        if(this.isFlywirePayment){
          this.$submitPaymentFlywire({
            ...pg_response.payment,
            tx_order_id: this.tx_id
          }, true, 'ACTIVITY');
        } else if (this.isRazorpayCurlecPayment) {
          this.$submitPaymentRazorpay({
            ...pg_response.payment,
            tx_order_id: this.tx_id,
            payment_config:{ channel,code}
          }, true, 'ACTIVITY')
        } else if(this.isCommercePay){
          const { pg_response } = this.payment
          this.$openLink(pg_response.payment.commercepay_payment_url)
        } else if(this.isPivotPayment){
          this.$openLink(this.$getPivotPaymentUrl(pg_response))
        } else {
          this.checkStatus()
        }
      }
    }
  }
</script>
<style>
  .img-check{
    height: 100px;
  }
  @media screen and (max-width: 400px){
    .img-check{
      height: 60px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/hotel/concierge/activity/PaymentResult.vue